import {
  Box,
  Divider,
  Text,
  Title,
  WizardStep,
  WizardStepRenderParams,
} from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import { HealthcarePlanData } from 'modules/benefits/components/benefitModals/HealthcarePlanWizard/types'

const SummaryStep = ({ data }: WizardStepRenderParams<HealthcarePlanData>) => {
  const {
    employee,
    access: { locale },
  } = useCurrentSession()

  // Get currency from employee's legal entity, fallback to EUR
  const currency = employee?.legalEntity?.currency ?? 'EUR'

  const formatCurrency = (euros: number) => {
    return MoneyWithCurrency({
      amount: toCents(euros),
      currency,
      locale,
    })
  }

  const formatWaitingPeriod = (months: number) => {
    if (months === 0)
      return i18n.t(
        'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.waiting_period.no_waiting_period'
      )
    return i18n.tp(
      'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.waiting_period',
      { count: months }
    )
  }

  const employeeTotalCost =
    data.employeeCostCents !== undefined ? fromCents(data.employeeCostCents) : 0
  const employeeCompanyCost =
    data.coveredEmployeeCostCents !== undefined ? fromCents(data.coveredEmployeeCostCents) : 0
  const employeePersonalCost = Math.max(0, employeeTotalCost - employeeCompanyCost)

  const hasDependents =
    data.dependentCostCents !== undefined && data.dependentCostCents > 0 && data.allowDependents
  const dependentTotalCost =
    hasDependents && data.dependentCostCents ? fromCents(data.dependentCostCents) : 0
  const dependentCompanyCost = hasDependents ? fromCents(data.coveredDependentCostCents || 0) : 0
  const dependentPersonalCost = Math.max(0, dependentTotalCost - dependentCompanyCost)

  const waitingPeriod = data.waitingPeriodMonths
    ? formatWaitingPeriod(data.waitingPeriodMonths)
    : ''

  return (
    <Box flexDirection="column" gap="s24" width="s360">
      <Box flexDirection="column" gap="s8">
        <Title
          title={i18n.t('benefits.healthcare.plan_wizard.summary_step.title')}
          description={i18n.t('benefits.healthcare.plan_wizard.summary_step.description')}
        />
      </Box>

      <Box flexDirection="column" gap="s32">
        {/* Plan Section */}
        <Box flexDirection="column" gap="s8">
          <Text.Header2>
            {i18n.t('benefits.healthcare.plan_wizard.summary_step.plan.label')}
          </Text.Header2>
          <Text.Normal weight="medium">
            {data.providerName} {data.planName}
          </Text.Normal>
        </Box>
        <Divider />
        {/* Renewal Date Section */}
        <Box flexDirection="column" gap="s8">
          <Text.Header2>
            {i18n.t('benefits.healthcare.plan_wizard.summary_step.renewal_date.label')}
          </Text.Header2>
          <Text.Normal weight="medium">{data.renewalDate}</Text.Normal>
        </Box>
        <Divider />
        {/* Enrollment Conditions Section */}
        <Box flexDirection="column" gap="s16">
          <Text.Header2>
            {i18n.t(
              'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.title'
            )}
          </Text.Header2>
          <Box flexDirection="column" gap="s8">
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal>
                {i18n.t(
                  'benefits.healthcare.plan_wizard.summary_step.enrollment_conditions.waiting_period.label'
                )}
              </Text.Normal>
              <Text.Normal weight="medium">{waitingPeriod}</Text.Normal>
            </Box>
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal>
                {i18n.t(
                  'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.pre_existing_conditions.label'
                )}
              </Text.Normal>
              <Text.Normal weight="medium">
                {data.allowPreExistingConditions
                  ? i18n.t(
                      'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.pre_existing_conditions.allowed'
                    )
                  : i18n.t(
                      'benefits.healthcare.plan_wizard.plan_details_step.enrollment_conditions.pre_existing_conditions.not_allowed'
                    )}
              </Text.Normal>
            </Box>
          </Box>
        </Box>
        <Divider />
        {/* Total Monthly Cost Section */}
        <Box flexDirection="column" gap="s16">
          <Text.Header2>
            {i18n.t('benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.label')}
          </Text.Header2>

          {/* Employee Costs */}
          <Box flexDirection="column" gap="s12">
            <Text.Normal weight="semibold">
              {i18n.t(
                'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.employee.label'
              )}
            </Text.Normal>
            <Box flexDirection="column" gap="s8">
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Text.Normal>
                  {i18n.t(
                    'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.employee.total_plan_cost.label'
                  )}
                </Text.Normal>
                <Text.Normal weight="medium">{formatCurrency(employeeTotalCost)}</Text.Normal>
              </Box>
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Text.Normal>
                  {i18n.t(
                    'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.employee.paid_by_company.label'
                  )}
                </Text.Normal>
                <Text.Normal weight="medium">{formatCurrency(employeeCompanyCost)}</Text.Normal>
              </Box>
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Text.Normal>
                  {i18n.t(
                    'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.employee.paid_by_employee.label'
                  )}
                </Text.Normal>
                <Text.Normal weight="medium">{formatCurrency(employeePersonalCost)}</Text.Normal>
              </Box>
            </Box>
          </Box>

          {/* Dependents Costs (if applicable) */}
          {hasDependents && (
            <Box flexDirection="column" gap="s12">
              <Text.Normal weight="semibold">
                {i18n.t(
                  'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.dependent.label'
                )}
              </Text.Normal>
              <Box flexDirection="column" gap="s8">
                <Box flexDirection="row" justifyContent="spaceBetween">
                  <Text.Normal>
                    {i18n.t(
                      'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.dependent.total_plan_cost.label'
                    )}
                  </Text.Normal>
                  <Text.Normal weight="medium">{formatCurrency(dependentTotalCost)}</Text.Normal>
                </Box>
                <Box flexDirection="row" justifyContent="spaceBetween">
                  <Text.Normal>
                    {i18n.t(
                      'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.dependent.paid_by_company.label'
                    )}
                  </Text.Normal>
                  <Text.Normal weight="medium">{formatCurrency(dependentCompanyCost)}</Text.Normal>
                </Box>
                <Box flexDirection="row" justifyContent="spaceBetween">
                  <Text.Normal>
                    {i18n.t(
                      'benefits.healthcare.plan_wizard.summary_step.total_monthly_cost.dependent.paid_by_employee.label'
                    )}
                  </Text.Normal>
                  <Text.Normal weight="medium">{formatCurrency(dependentPersonalCost)}</Text.Normal>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// Define step as factory function (like working wizards) instead of direct object
export const summaryStep = (): WizardStep<HealthcarePlanData> => ({
  title: i18n.t('benefits.healthcare.plan_wizard.summary_step.title'),
  render: (props) => <SummaryStep {...props} />,
  nextText: i18n.t('common.save'),
  isCompleted: () => true,
})
